import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Star, Plus, ArrowRight, CalendarDays, Box, Baby, Gift } from "lucide-react";
import { toast } from "sonner";
import api, { formatPrice } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { MealCard } from "@/components/MealCard";
import { RewardProgress } from "@/components/RewardProgress";
import { Loader } from "@/components/Loader";

export default function HomePage() {
  const navigate = useNavigate();
  const { addItem, totalQty, preview } = useCart();
  const [motd, setMotd] = useState(null);
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [m, f] = await Promise.all([
          api.get("/meals/meal-of-day"),
          api.get("/meals/favorites"),
        ]);
        setMotd(m.data.meal);
        setFavorites(f.data.meals || []);
      } catch (e) {
        toast.error("Could not load the menu.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <Loader label="Plating today's specials…" />;

  const qty = preview?.total_qty ?? totalQty;
  const motdRemaining = qty === 0 ? 5 : (5 - (qty % 5)) % 5;

  const shortcuts = [
    { to: "/planner", label: "Plan the Week", icon: CalendarDays, color: "bg-sky-100 text-sky-600" },
    { to: "/builder", label: "Build a Box", icon: Box, color: "bg-amber-100 text-amber-600" },
    { to: "/children", label: "My Kids", icon: Baby, color: "bg-purple-100 text-purple-600" },
  ];

  return (
    <div className="space-y-8">
      {/* Buy 4, Get 5th Free — hero promo banner */}
      <div
        data-testid="reward-hero-banner"
        onClick={() => navigate("/menu")}
        className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-emerald-500 via-emerald-500 to-rose-400 p-6 sm:p-8 shadow-lg shadow-emerald-200/60 cursor-pointer group"
      >
        <div className="absolute -right-6 -top-8 h-40 w-40 rounded-full bg-white/10" />
        <div className="absolute right-16 -bottom-10 h-32 w-32 rounded-full bg-white/10" />
        <div className="relative z-10 flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6">
          <span className="inline-flex items-center justify-center h-16 w-16 shrink-0 rounded-3xl bg-white/20 backdrop-blur-sm text-white">
            <Gift size={34} strokeWidth={2.2} />
          </span>
          <div className="flex-1 min-w-0 text-white">
            <span className="inline-block bg-white/25 text-white text-xs font-bold uppercase tracking-wide px-3 py-1 rounded-full mb-2">
              Loyalty Reward
            </span>
            <h2 className="font-display font-extrabold text-2xl sm:text-3xl lg:text-4xl leading-tight drop-shadow-sm">
              Buy 4 Meals, Get Your 5th FREE!
            </h2>
            <p className="text-white/90 mt-1.5 font-medium text-sm sm:text-base">
              {motdRemaining === 0
                ? "🎉 You've unlocked a free meal — it's applied automatically at checkout!"
                : `Add ${motdRemaining} more meal${motdRemaining > 1 ? "s" : ""} to unlock a free one. Discount applies automatically.`}
            </p>
          </div>
          <button
            data-testid="reward-hero-cta"
            className="shrink-0 inline-flex items-center gap-2 bg-white text-emerald-600 font-display font-bold rounded-2xl px-5 py-3 shadow-md group-hover:scale-105 transition-transform"
          >
            Start ordering <ArrowRight size={18} strokeWidth={3} />
          </button>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Meal of the day hero */}
        {motd && (
          <div
            data-testid="meal-of-day"
            className="lg:col-span-2 relative rounded-3xl overflow-hidden border-2 border-emerald-100 shadow-lg min-h-[300px] flex"
          >
            <img src={motd.image_url} alt={motd.name} className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
            <div className="relative z-10 mt-auto p-6 sm:p-8 text-white w-full">
              <span className="inline-flex items-center gap-1.5 bg-rose-400 text-white text-xs font-bold px-3 py-1 rounded-full mb-3">
                ✨ Meal of the Day
              </span>
              <h1 className="font-display font-bold text-3xl sm:text-4xl tracking-tight">{motd.name}</h1>
              <p className="mt-2 text-white/90 max-w-lg line-clamp-2">{motd.description}</p>
              <div className="flex flex-wrap items-center gap-4 mt-4">
                <span className="inline-flex items-center gap-1 font-bold">
                  <Star size={18} className="fill-amber-400 text-amber-400" />
                  {(motd.kid_rating || 0).toFixed(1)}
                </span>
                <span className="font-display font-bold text-2xl">{formatPrice(motd.price_cents)}</span>
                <button
                  data-testid="motd-add-button"
                  onClick={() => {
                    addItem(motd, 1);
                    toast.success(`${motd.name} added to cart!`);
                  }}
                  className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white font-bold rounded-2xl px-5 py-2.5 shadow-md transition-all active:scale-95"
                >
                  <Plus size={18} strokeWidth={3} /> Add to Cart
                </button>
                <button
                  data-testid="motd-detail-button"
                  onClick={() => navigate(`/meal/${motd.meal_id}`)}
                  className="inline-flex items-center gap-1 font-semibold text-white/90 hover:text-white"
                >
                  Details <ArrowRight size={16} />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Reward + shortcuts */}
        <div className="space-y-4">
          <RewardProgress totalQty={preview?.total_qty ?? totalQty} freeCount={preview?.free_count ?? 0} />
          <div className="grid grid-cols-3 gap-3">
            {shortcuts.map((s) => (
              <button
                key={s.to}
                data-testid={`shortcut-${s.label.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => navigate(s.to)}
                className="bg-white rounded-3xl border-2 border-emerald-100/80 p-4 flex flex-col items-center gap-2 hover:-translate-y-1 hover:shadow-lg transition-all"
              >
                <span className={`h-11 w-11 rounded-2xl flex items-center justify-center ${s.color}`}>
                  <s.icon size={20} />
                </span>
                <span className="font-display font-semibold text-xs text-slate-700 text-center leading-tight">
                  {s.label}
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Kids favorites */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display font-bold text-2xl text-slate-800 tracking-tight">
            ⭐ Kids' Favorites
          </h2>
          <button
            data-testid="see-full-menu"
            onClick={() => navigate("/menu")}
            className="inline-flex items-center gap-1 font-semibold text-emerald-600 hover:text-emerald-700"
          >
            Full menu <ArrowRight size={16} />
          </button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {favorites.map((m) => (
            <MealCard key={m.meal_id} meal={m} />
          ))}
        </div>
      </section>
    </div>
  );
}
